import { useEffect } from "react";

/* Title, description and share tags of one page.
   index.html holds the tags of the site. A page that needs its own (an
   article) changes them while it is open and puts them back when it closes.
   Search engines that run JavaScript read these. Link previews in chat and
   social apps usually read index.html as it is served, before any script
   runs, so they show the tags of the site unless the host prerenders pages. */
export const SITE_URL = "https://lorenzobernardi.xyz";

export interface PageMeta {
  title: string;
  description?: string;
  /** Path of the page, for the canonical address. */
  path: string;
  type?: "website" | "article";
  image?: string;
  imageAlt?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

type Undo = () => void;

function setTag(selector: string, create: () => HTMLElement, attribute: string, value: string): Undo {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
    const added = el;
    added.setAttribute(attribute, value);
    return () => added.remove();
  }
  const found = el;
  const before = found.getAttribute(attribute);
  found.setAttribute(attribute, value);
  return () => (before === null ? found.removeAttribute(attribute) : found.setAttribute(attribute, before));
}

const meta = (key: "name" | "property", name: string, content: string) =>
  setTag(
    `meta[${key}="${name}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute(key, name);
      return el;
    },
    "content",
    content,
  );

export function applyPageMeta(page: PageMeta): Undo {
  const undo: Undo[] = [];
  const url = SITE_URL + page.path;
  const title = `${page.title} | Lorenzo Bernardi`;

  const titleBefore = document.title;
  document.title = title;
  undo.push(() => {
    document.title = titleBefore;
  });

  undo.push(
    setTag(
      'link[rel="canonical"]',
      () => {
        const el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        return el;
      },
      "href",
      url,
    ),
  );
  undo.push(meta("property", "og:title", title), meta("name", "twitter:title", title));
  undo.push(meta("property", "og:url", url), meta("property", "og:type", page.type ?? "website"));
  if (page.description) {
    undo.push(
      meta("name", "description", page.description),
      meta("property", "og:description", page.description),
      meta("name", "twitter:description", page.description),
    );
  }
  if (page.image) {
    // Social sites need a full address. The pictures now live on this site, under /images.
    const image = page.image.startsWith("/") ? `${SITE_URL}${page.image}` : page.image;
    undo.push(meta("property", "og:image", image), meta("name", "twitter:image", image));
    // the size in index.html belongs to the picture of the site
    for (const size of ["og:image:width", "og:image:height"]) {
      const el = document.head.querySelector(`meta[property="${size}"]`);
      if (el) {
        const parent = el.parentNode;
        const next = el.nextSibling;
        el.remove();
        undo.push(() => parent?.insertBefore(el, next));
      }
    }
    if (page.imageAlt) undo.push(meta("property", "og:image:alt", page.imageAlt), meta("name", "twitter:image:alt", page.imageAlt));
  }
  if (page.publishedTime) undo.push(meta("property", "article:published_time", page.publishedTime));
  if (page.modifiedTime) undo.push(meta("property", "article:modified_time", page.modifiedTime));

  return () => undo.reverse().forEach((fn) => fn());
}

export function usePageMeta(page: PageMeta) {
  const { title, description, path, type, image, imageAlt, publishedTime, modifiedTime } = page;
  useEffect(
    () => applyPageMeta({ title, description, path, type, image, imageAlt, publishedTime, modifiedTime }),
    [title, description, path, type, image, imageAlt, publishedTime, modifiedTime],
  );
}
